import * as turf from "@turf/turf";
import { GameMap } from "../map";
import { GameClient, cryptoId, runDisplay, finalMinutes } from "../net";
import { STATION_BY_ID } from "../stations";
import { overrideKey, bakedZoneGeometry } from "../geo";
import { logout, geocodeArea, adminAction, getGameMembers, inviteToGameApi, addRosterPlayerApi, rosterLinkApi, setMemberTeam, removeMember, enterGameSession, viewAsPlayer, type GameMember, type MemberTeam, type PendingInvite } from "../api";
import { mountInfo } from "./info";
import { mountGameMenu } from "./gameMenu";
import { GAME_SIZES } from "../curses";
import { deckSettingsHtml, wireDeckSettings } from "./deckSettings";
import { esc, fmtMin } from "./util";

// Mobile-native MTA-themed Gamemaster console. A scrollable card layout (NOT a
// map-first shell): ink header with the hider's live run + clock, team & player
// cards with Make-hider / Move, game-settings steppers & toggles, and a live
// scoring table. The map (off-limits drawing + per-station zone corners) opens
// as a full-screen overlay. See design-handoff/ for the spec.

const PHASE: Record<string, { l: string; c: string }> = {
  idle: { l: "NOT STARTED", c: "#8C7E64" },
  headstart: { l: "HEAD START", c: "#FF6319" },
  running: { l: "HIDING", c: "#00933C" },
  paused: { l: "PAUSED", c: "#8C7E64" },
  stopped: { l: "CAUGHT", c: "#EE352E" },
};
const ZONE_MILES: [string, string][] = [["quarter", "¼ mi"], ["sixth", "⅙ mi"], ["eighth", "⅛ mi"]];
const ZONE_BLOCKS: [string, string][] = [["block1", "1 blk"], ["block2", "2 blk"], ["block3", "3 blk"]];
const TEAM_COLORS = ["#0039A6", "#EE352E", "#00933C", "#FF6319", "#B933AD", "#FCCC0A"];

export function mountGm(root: HTMLElement, client: GameClient, opts: { onExit?: () => void; title: string }) {
  document.body.classList.add("sk-active");
  let members: GameMember[] = [], pending: PendingInvite[] = [], teamList: MemberTeam[] = [];
  let moveId: string | null = null; // member (userId) being reassigned
  // ---- map overlay state ----
  let gameMap: GameMap | null = null;
  let drawing = false, ring: [number, number][] = [];
  let zoneSel: string | null = null, editing = false, editTeardown: (() => void) | null = null;

  root.innerHTML = `
    <div class="sk gm">
      <div class="gm-head">
        <div class="gm-head-top">
          <div class="gm-brand"><span class="gm-avatar">G</span>
            <div><div class="gm-title">GAMEMASTER</div><div class="gm-sub">Jet Lag NYC · <span id="gm-sync">live</span></div></div>
          </div>
          <div class="gm-head-btns">
            ${client.identity.acct ? `<button class="gm-back" id="gm-play" title="Join the game as a player and view your own board">Play ▸</button>` : ""}
            <button class="nav-btn" id="game-menu-btn" aria-label="Open menu"><span></span><span></span><span></span></button>
          </div>
        </div>
        <div class="gm-statusrow">
          <div class="gm-statuspill" id="gm-status"><span></span>—</div>
          <div class="gm-clock" id="gm-clock">--:--</div>
          <button class="gm-mapbtn" id="gm-run">Start run</button>
          <button class="gm-mapbtn" id="gm-openmap">Map →</button>
        </div>
      </div>
      <div class="gm-body jl-scroll" id="gm-body"></div>

      <div class="gm-mapwrap" id="gm-mapwrap" hidden>
        <div id="map" class="sk-map"></div>
        <div class="gm-maptop">
          <button class="gm-mapback" id="gm-mapback">← Console</button>
          <div class="gm-maphint" id="gm-maphint">Tap a station to edit its zone · or draw an off-limits area</div>
        </div>
        <div class="gm-mapctl" id="gm-mapctl"></div>
      </div>
    </div>`;

  mountInfo(client);
  mountGameMenu(root, client, { onExit: opts.onExit });
  root.querySelector("#gm-play")?.addEventListener("click", async () => {
    let teamId = members.find((m) => m.userId === client.identity.sub)?.teamId;
    if (!teamId) {
      const ts = teams();
      if (!ts.length) { alert("Add a team first, then you can play on it."); return; }
      const choice = prompt("Play on which team?\n" + ts.map((t, i) => `${i + 1}. ${t.name} (${t.role})`).join("\n"));
      const idx = choice ? parseInt(choice, 10) - 1 : -1;
      if (!(idx >= 0 && idx < ts.length)) return;
      teamId = ts[idx].id;
      try { await setMemberTeam(client.gameId, client.identity.sub, teamId); } catch (e) { alert((e as Error).message); return; }
    }
    try { await enterGameSession(client.gameId, "player"); location.reload(); } catch (e) { alert((e as Error).message); }
  });
  root.querySelector("#gm-openmap")!.addEventListener("click", () => openMap());
  root.querySelector("#gm-run")!.addEventListener("click", (e) => {
    const act = (e.target as HTMLElement).dataset.act || "start";
    if (act === "stop" && !confirm("Stop the current hider's run? This banks their time.")) return;
    client.runOp(act);
  });
  root.querySelector("#gm-mapback")!.addEventListener("click", () => closeMap());

  const teams = () => teamList;
  const hiderKey = () => (client.mode === "group" ? "__hiders__" : client.hiderTeamId || "");
  const PLAYGM_KEY = "jl_playgm_" + client.gameId;
  let playingGm = localStorage.getItem(PLAYGM_KEY) === "1";
  let spotRevealed = false;
  async function reloadRoster() {
    try { const d = await getGameMembers(client.gameId); members = d.members; pending = d.pending; teamList = d.teams; render(); } catch { /* ignore */ }
  }
  reloadRoster();
  // createTeam / deleteTeam still go through the gamemaster admin RPC.
  async function act(action: string, params: Record<string, unknown>) {
    try { await adminAction(action, params); await reloadRoster(); } catch (e) { alert((e as Error).message); }
  }

  // ---- header status (hider's run) --------------------------------------
  function renderHead() {
    const d = runDisplay(client.runsAll[hiderKey()] ?? null, client.config.headStartSec);
    const p = PHASE[d.phase];
    const pill = root.querySelector<HTMLElement>("#gm-status")!;
    pill.innerHTML = `<span style="background:${p.c}"></span>${p.l}`;
    pill.style.color = p.c;
    root.querySelector<HTMLElement>("#gm-clock")!.textContent = d.text;
    root.querySelector<HTMLElement>("#gm-sync")!.textContent = client.connected ? "live" : "reconnecting…";
    const runBtn = root.querySelector<HTMLButtonElement>("#gm-run");
    if (runBtn) { const idle = d.phase === "idle" || d.phase === "stopped"; runBtn.textContent = idle ? "▶ Start run" : "⏹ Stop run"; runBtn.dataset.act = idle ? "start" : "stop"; }
  }

  // ---- console body ------------------------------------------------------
  function render() {
    const ae = document.activeElement;
    if (ae && root.contains(ae) && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName)) { renderHead(); return; }
    renderHead();
    const seekingteams = client.mode !== "group";
    const body = root.querySelector<HTMLElement>("#gm-body")!;
    body.innerHTML = `
      ${client.mode === "group" ? groupBlock() : teamsBlock()}
      ${renderYou()}

      <div class="gm-seclabel gm-sectop">GAME SETTINGS</div>
      <div class="gm-settings">
        <div class="gm-set-row">
          <div class="gm-set-info"><div class="gm-set-name">Head start</div><div class="gm-set-cap">Hider lead before seeking begins</div></div>
          <div class="gm-stepper"><button data-hs="-5">−</button><div class="gm-step-val">${Math.round(client.config.headStartSec / 60)}m</div><button data-hs="5">+</button></div>
        </div>
        <div class="gm-set-block">
          <div class="gm-set-name">Hiding-zone size</div>
          <div class="gm-zbtns">${ZONE_MILES.map(([v, l]) => `<button data-zone="${v}" class="${client.config.zoneMode === v ? "on" : ""}">${l}</button>`).join("")}</div>
          <div class="gm-zbtns">${ZONE_BLOCKS.map(([v, l]) => `<button data-zone="${v}" class="${client.config.zoneMode === v ? "on" : ""}">${l}</button>`).join("")}</div>
          <button class="gm-set-wide" data-openmap>Edit per-station corners on map →</button>
        </div>
        <div class="gm-set-block">
          <div class="gm-set-name">Game size</div>
          <div class="gm-set-cap" style="margin:-2px 0 6px">Scales curse-card time values</div>
          <div class="gm-zbtns">${GAME_SIZES.map((g) => `<button data-gsize="${g.id}" class="${client.gameSize === g.id ? "on" : ""}">${g.label}</button>`).join("")}</div>
        </div>
        ${deckSettingsHtml(client)}
        <div class="gm-set-row">
          <div class="gm-set-info"><div class="gm-set-name">Matching assist mode</div><div class="gm-set-cap">Enables players' “How close am I?”</div></div>
          <button class="gm-switch ${client.config.matchingAssist ? "on" : ""}" id="gm-assist"><span></span></button>
        </div>
        <div class="gm-set-block">
          <div class="gm-set-rowh"><div class="gm-set-name">Off-limits areas</div><span class="gm-secnote">${client.offLimits.length}</span></div>
          <div class="gm-offlist">${client.offLimits.map((o) => `<div class="gm-offrow"><span class="gm-offdot"></span><span class="gm-offname">${esc(o.name)}</span><button class="gm-offx" data-rmoff="${o.id}">×</button></div>`).join("") || `<div class="sk-empty" style="padding:6px 2px">None set.</div>`}</div>
          <div class="gm-offadd"><button data-offaddr>+ Address</button><button data-openmap>+ Draw on map</button></div>
        </div>
      </div>

      <div class="gm-seclabel gm-sectop">SCORING</div>
      ${seekingteams ? `<div class="gm-bonusrow">Seeking bonus <input id="gm-bonus" type="number" min="0" value="${client.config.bonusPct}"/> % per point</div>` : ""}
      <div class="gm-scorewrap">
        <div class="gm-score-head"><div class="c-team">${seekingteams ? "TEAM" : "HIDERS"}</div><div class="c-run">RUN</div><div class="c-card">CARDS</div>${seekingteams ? `<div class="c-seek">SEEK</div>` : ""}<div class="c-total">TOTAL</div></div>
        ${scoreRows().map((r, i) => scoreRow(r, i, seekingteams)).join("") || `<div class="sk-empty" style="padding:12px">No teams yet.</div>`}
        <div class="gm-score-note">Run + card minutes are the hider's banked time; seeking points add to the seekers' total.</div>
      </div>

      <div class="gm-seclabel gm-sectop">MAP, ZONES &amp; OFF-LIMITS</div>
      <button class="gm-maprow" data-openmap><span>Open full map &amp; layers →</span></button>

      <button class="sk-logout" id="gm-logout">Log out</button>`;
    wire(body);
  }

  const pendingRow = (p: PendingInvite) => `<div class="gm-player"><span class="gm-avatar2">✉</span><div class="gm-player-id"><div class="gm-player-name">${esc(p.email)}</div><div class="gm-player-mail">invited — not signed up yet</div></div></div>`;

  // The GM is also a player: spoiler-safe "Playing GM" controls.
  function renderYou(): string {
    const myTeam = members.find((m) => m.userId === client.identity.sub)?.teamId;
    const myTeamName = myTeam ? (teams().find((t) => t.id === myTeam)?.name ?? "your team") : "";
    const sid = client.hiderSpot?.stationId;
    const spotName = sid ? (STATION_BY_ID[sid]?.name ?? sid) : null;
    const reveal = !playingGm || spotRevealed;
    const canPlay = client.mode === "group" || !!myTeam;
    const playRow = canPlay
      ? `<button class="gm-back" id="gm-play2">Play ▸</button>`
      : `<button class="gm-back" id="gm-join">➕ Join a team</button>`;
    return `<div class="gm-seclabel gm-sectop">PLAYING GM</div>
      <div class="gm-settings">
        <div class="gm-set-row"><div class="gm-set-info"><div class="gm-set-name">I'm playing too</div><div class="gm-set-cap">Hides the hider's spot &amp; locations behind a warning</div></div><button class="gm-switch ${playingGm ? "on" : ""}" id="gm-playgm"><span></span></button></div>
        <div class="gm-set-row"><div class="gm-set-info"><div class="gm-set-name">Your player slot</div><div class="gm-set-cap">${myTeam ? esc(myTeamName) : (client.mode === "group" ? "Seeker · shared board" : "Not on a team yet")}</div></div>${playRow}</div>
        <div class="gm-set-block"><div class="gm-set-rowh"><div class="gm-set-name">Current hider's spot</div></div>
          ${spotName ? (reveal ? `<div class="gm-spotval">📍 ${esc(spotName)}${client.hiderSpot?.confirmed ? " · locked in" : " · not locked"}</div>` : `<button class="gm-set-wide gm-spoiler" id="gm-revealspot">⚠️ You're playing — tap to reveal the hider's spot</button>`) : `<div class="sk-empty" style="padding:6px 2px">Hider hasn't picked a spot yet.</div>`}
        </div>
      </div>`;
  }

  // Seekingteams/individual: team cards + Unassigned bucket + Add-a-team.
  function teamsBlock(): string {
    const ts = teams(), ps = members;
    const unassigned = ps.filter((m) => !m.isGm && !m.teamId);
    return `
      <div class="gm-sechead"><span class="gm-seclabel">TEAMS &amp; PLAYERS</span><span class="gm-secnote">${ts.length} teams · ${ps.length} member${ps.length === 1 ? "" : "s"}</span></div>
      <div class="gm-teams">
        ${ts.map((t, i) => teamCard(t, i, ps)).join("")}
        ${unassigned.length || pending.length ? `<div class="gm-team"><div class="gm-team-h"><span class="gm-bullet" style="background:#8C7E64">?</span><div class="gm-team-id"><div class="gm-team-name">Unassigned</div><div class="gm-team-sub">${unassigned.length} member${unassigned.length === 1 ? "" : "s"}${pending.length ? ` · ${pending.length} invited` : ""}</div></div></div>
          <div class="gm-players">
            ${unassigned.map((m) => memberRow(m)).join("")}
            ${pending.map(pendingRow).join("")}
          </div></div>` : ""}
        <button class="gm-addteam" id="gm-addteam">+ Add a team</button>
        <button class="gm-addplayer" data-addname="" style="margin-top:6px">+ Add a player by name</button>
      </div>`;
  }

  // Group mode: one shared group; tap a player to toggle hider (others seek).
  function groupBlock(): string {
    const nonGm = members.filter((m) => !m.isGm);
    const hiding = nonGm.filter((m) => client.hiderSet.includes(m.userId)).length;
    return `
      <div class="gm-sechead"><span class="gm-seclabel">GROUP</span><span class="gm-secnote">${nonGm.length} player${nonGm.length === 1 ? "" : "s"} · ${hiding} hiding</span></div>
      <div class="gm-team ${hiding ? "hider" : ""}">
        <div class="gm-team-h"><span class="gm-bullet" style="background:#231C12">●</span><div class="gm-team-id"><div class="gm-team-name">Everyone</div><div class="gm-team-sub">Tap a player to toggle <b>Hider</b> — the rest seek on one shared board</div></div></div>
        <div class="gm-players">
          ${nonGm.map((m) => groupRow(m, client.hiderSet.includes(m.userId))).join("") || `<div class="sk-empty" style="padding:10px">No players yet — add some below.</div>`}
          ${pending.map(pendingRow).join("")}
          <button class="gm-addplayer" data-addname="">+ Add by name</button>
          <button class="gm-addplayer" data-invite="">+ Invite by email</button>
        </div>
      </div>`;
  }
  function groupRow(m: GameMember, hiding: boolean): string {
    const av = m.photo ? `<span class="gm-avatar2" style="background-image:url('${m.photo}');background-size:cover"></span>` : `<span class="gm-avatar2">${esc((m.name[0] || "?").toUpperCase())}</span>`;
    return `<div class="gm-player">${av}
      <div class="gm-player-id"><div class="gm-player-name">${esc(m.name)}</div><div class="gm-player-mail">${esc(m.email || "")}</div></div>
      <button class="gm-view" data-viewas="${m.userId}" title="See this player's view">👁</button>
      <button class="gm-view" data-link="${m.userId}" title="Copy this player's join link">🔗</button>
      <button class="gm-rolebtn ${hiding ? "hider" : ""}" data-togglehider="${m.userId}">${hiding ? "🔴 Hider" : "Seeker"}</button>
    </div>`;
  }

  function memberRow(m: GameMember): string {
    const av = m.photo ? `<span class="gm-avatar2" style="background-image:url('${m.photo}');background-size:cover"></span>` : `<span class="gm-avatar2">${esc((m.name[0] || "?").toUpperCase())}</span>`;
    return `<div class="gm-player">${av}
      <div class="gm-player-id"><div class="gm-player-name">${esc(m.name)}</div><div class="gm-player-mail">${esc(m.email || "")}</div></div>
      <button class="gm-view" data-viewas="${m.userId}" title="See this player's view">👁</button>
      <button class="gm-view" data-link="${m.userId}" title="Copy this player's join link">🔗</button>
      <button class="gm-move" data-move="${m.userId}">Move</button>
    </div>`;
  }

  function teamCard(t: MemberTeam, i: number, ps: GameMember[]): string {
    const col = TEAM_COLORS[i % TEAM_COLORS.length];
    const isHider = t.id === client.hiderTeamId;
    const mine = ps.filter((p) => p.teamId === t.id);
    return `<div class="gm-team ${isHider ? "hider" : ""}">
      <div class="gm-team-h">
        <span class="gm-bullet" style="background:${col}">${esc((t.name[0] || "?").toUpperCase())}</span>
        <div class="gm-team-id"><div class="gm-team-name">${esc(t.name)}</div><div class="gm-team-sub">${t.role}${isHider ? " · CURRENT HIDER" : ""} · ${mine.length} member${mine.length === 1 ? "" : "s"}</div></div>
        ${isHider ? `<span class="gm-hiderchip">HIDER</span>` : `<button class="gm-hiderbtn" data-makehider="${t.id}">Make hider</button>`}
      </div>
      <div class="gm-players">
        ${mine.map((m) => memberRow(m)).join("")}
        <button class="gm-addplayer" data-addname="${t.id}">+ Add by name</button>
        <button class="gm-addplayer" data-invite="${t.id}">+ Invite by email</button>
      </div>
    </div>`;
  }

  function scoreRows(): { key: string; name: string; col: string; seek: boolean }[] {
    return client.mode !== "group"
      ? teams().map((t, i) => ({ key: t.id, name: t.name, col: TEAM_COLORS[i % TEAM_COLORS.length], seek: true }))
      : [{ key: "__hiders__", name: client.hiderSet.map((id) => members.find((m) => m.userId === id)?.name ?? "?").join(", ") || "(no hiders yet)", col: "#EE352E", seek: false }];
  }
  function scoreRow(r: { key: string; name: string; col: string; seek: boolean }, _i: number, seekingteams: boolean): string {
    const sc = client.scoresAll[r.key] ?? { cardBonusMin: 0, seekingPoints: 0 };
    const d = runDisplay(client.runsAll[r.key] ?? null, client.config.headStartSec);
    const fin = finalMinutes(d.runMs, sc, client.config.bonusPct, seekingteams);
    return `<div class="gm-score-row">
      <div class="c-team"><span class="gm-sdot" style="background:${r.col}"></span><span class="gm-sname">${esc(r.name)}</span></div>
      <div class="c-run" data-run-cell="${r.key}">${fmtMin(d.runMs / 60000)}</div>
      <div class="c-card"><input class="gm-scin" type="number" min="0" data-card="${r.key}" value="${sc.cardBonusMin}"/></div>
      ${r.seek ? `<div class="c-seek"><input class="gm-scin" type="number" min="0" data-pts="${r.key}" value="${sc.seekingPoints}"/></div>` : ""}
      <div class="c-total" data-final-cell="${r.key}">${fmtMin(fin)}</div>
    </div>`;
  }

  function wire(body: HTMLElement) {
    body.querySelectorAll<HTMLButtonElement>("[data-openmap]").forEach((b) => b.addEventListener("click", () => openMap()));
    body.querySelector("#gm-logout")!.addEventListener("click", () => logout());
    // teams & players
    body.querySelectorAll<HTMLButtonElement>("[data-makehider]").forEach((b) => b.addEventListener("click", () => {
      const t = teams().find((x) => x.id === b.dataset.makehider);
      if (t && confirm(`Make "${t.name}" the hider? The ongoing run is archived and a new round starts.`)) client.setHiderTeam(t.id);
    }));
    body.querySelectorAll<HTMLButtonElement>("[data-move]").forEach((b) => b.addEventListener("click", () => { moveId = b.dataset.move!; renderMove(); }));
    body.querySelectorAll<HTMLButtonElement>("[data-viewas]").forEach((b) => b.addEventListener("click", async () => { try { await viewAsPlayer(client.gameId, b.dataset.viewas!); location.reload(); } catch (e) { alert((e as Error).message); } }));
    body.querySelector("#gm-playgm")?.addEventListener("click", () => { playingGm = !playingGm; spotRevealed = false; localStorage.setItem(PLAYGM_KEY, playingGm ? "1" : "0"); render(); });
    body.querySelector("#gm-revealspot")?.addEventListener("click", () => { spotRevealed = true; render(); });
    body.querySelector("#gm-play2")?.addEventListener("click", async () => { try { await enterGameSession(client.gameId, "player"); location.reload(); } catch (e) { alert((e as Error).message); } });
    body.querySelector("#gm-join")?.addEventListener("click", async () => {
      const ts = teams();
      if (!ts.length) { alert("Add a team first, then you can join it."); return; }
      const choice = prompt("Join which team to play on?\n" + ts.map((t, i) => `${i + 1}. ${t.name} (${t.role})`).join("\n"));
      const idx = choice ? parseInt(choice, 10) - 1 : -1;
      if (!(idx >= 0 && idx < ts.length)) return;
      try { await setMemberTeam(client.gameId, client.identity.sub, ts[idx].id); playingGm = true; localStorage.setItem(PLAYGM_KEY, "1"); await reloadRoster(); }
      catch (e) { alert((e as Error).message); }
    });
    body.querySelectorAll<HTMLButtonElement>("[data-invite]").forEach((b) => b.addEventListener("click", async () => {
      const raw = prompt("Invite by email — paste one or many (commas, spaces, or new lines):");
      if (!raw) return;
      const emails = [...new Set(raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean))];
      if (!emails.length) return;
      let added = 0, pending = 0, failed = 0;
      for (const email of emails) {
        try { const r = await inviteToGameApi(client.gameId, email, b.dataset.invite); if (r.added) added++; else pending++; }
        catch { failed++; }
      }
      alert(`${emails.length} invited — ${added} added now, ${pending} emailed a link${failed ? `, ${failed} failed` : ""}.`);
      await reloadRoster();
    }));
    body.querySelectorAll<HTMLButtonElement>("[data-link]").forEach((b) => b.addEventListener("click", async () => {
      try {
        const link = await rosterLinkApi(client.gameId, b.dataset.link!);
        try { await navigator.clipboard.writeText(link); alert("Join link copied — paste it to the player:\n\n" + link); }
        catch { prompt("Copy this player's join link:", link); }
      } catch (e) { alert((e as Error).message); }
    }));
    body.querySelectorAll<HTMLButtonElement>("[data-addname]").forEach((b) => b.addEventListener("click", async () => {
      const name = prompt("Add a player by name (no email needed):")?.trim(); if (!name) return;
      try {
        const { userId } = await addRosterPlayerApi(client.gameId, name);
        const teamId = b.dataset.addname; if (teamId) await setMemberTeam(client.gameId, userId, teamId);
        await reloadRoster();
        const link = await rosterLinkApi(client.gameId, userId);
        try { await navigator.clipboard.writeText(link); alert(`Added ${name}. Join link copied — paste it to them:\n\n` + link); }
        catch { prompt(`Added ${name}. Copy their join link:`, link); }
      } catch (e) { alert((e as Error).message); }
    }));
    body.querySelector("#gm-addteam")?.addEventListener("click", async () => {
      const name = prompt("New team name")?.trim(); if (!name) return;
      const role = confirm("Is this the HIDER team? (Cancel = seeker)") ? "hider" : "seeker";
      await act("createTeam", { gameId: client.gameId, name, role });
    });
    // group mode: toggle a player in/out of the hider set (starts a new round)
    body.querySelectorAll<HTMLButtonElement>("[data-togglehider]").forEach((b) => b.addEventListener("click", () => {
      const id = b.dataset.togglehider!;
      const set = new Set(client.hiderSet);
      const becomingHider = !set.has(id);
      becomingHider ? set.add(id) : set.delete(id);
      const who = members.find((m) => m.userId === id)?.name || "this player";
      if (confirm(`${becomingHider ? `Make ${who} a hider` : `Make ${who} a seeker`}? This archives the current run and starts a fresh round.`)) client.setHiders([...set]);
    }));
    // settings
    body.querySelectorAll<HTMLButtonElement>("[data-hs]").forEach((b) => b.addEventListener("click", () => {
      const cur = Math.round(client.config.headStartSec / 60);
      client.setHeadStart(Math.max(0, cur + Number(b.dataset.hs)) * 60);
    }));
    body.querySelectorAll<HTMLButtonElement>("[data-zone]").forEach((b) => b.addEventListener("click", () => client.setZoneMode(b.dataset.zone!)));
    body.querySelectorAll<HTMLButtonElement>("[data-gsize]").forEach((b) => b.addEventListener("click", () => client.setGameSize(b.dataset.gsize as "S" | "M" | "L")));
    wireDeckSettings(body, client);
    body.querySelector("#gm-assist")!.addEventListener("click", () => client.setMatchingAssist(!client.config.matchingAssist));
    body.querySelectorAll<HTMLButtonElement>("[data-rmoff]").forEach((b) => b.addEventListener("click", () => client.offLimitsOp({ op: "remove", id: b.dataset.rmoff })));
    body.querySelectorAll<HTMLButtonElement>("[data-offaddr]").forEach((b) => b.addEventListener("click", async () => {
      const q = prompt("Off-limits place (e.g. Central Park)")?.trim(); if (!q) return;
      try { const { name, geometry } = await geocodeArea(q); client.offLimitsOp({ op: "add", area: { id: cryptoId(), name, geometry } }); } catch (e) { alert((e as Error).message); }
    }));
    body.querySelector<HTMLInputElement>("#gm-bonus")?.addEventListener("change", (e) => client.setBonusPct(Math.max(0, +(e.target as HTMLInputElement).value || 0)));
    body.querySelectorAll<HTMLInputElement>("[data-card]").forEach((inp) => inp.addEventListener("change", () => client.setScore(inp.dataset.card!, { cardBonusMin: Math.max(0, parseInt(inp.value, 10) || 0) })));
    body.querySelectorAll<HTMLInputElement>("[data-pts]").forEach((inp) => inp.addEventListener("change", () => client.setScore(inp.dataset.pts!, { seekingPoints: Math.max(0, parseInt(inp.value, 10) || 0) })));
  }

  // ---- live scoring tick (don't rebuild → preserve focus) ---------------
  function tick() {
    renderHead();
    const seekingteams = client.mode !== "group";
    for (const r of scoreRows()) {
      const sc = client.scoresAll[r.key] ?? { cardBonusMin: 0, seekingPoints: 0 };
      const d = runDisplay(client.runsAll[r.key] ?? null, client.config.headStartSec);
      const runCell = root.querySelector(`[data-run-cell="${r.key}"]`);
      const finCell = root.querySelector(`[data-final-cell="${r.key}"]`);
      if (runCell) runCell.textContent = fmtMin(d.runMs / 60000);
      if (finCell) finCell.textContent = fmtMin(finalMinutes(d.runMs, sc, client.config.bonusPct, seekingteams));
    }
  }

  // ---- move-player sheet -------------------------------------------------
  function renderMove() {
    const existing = root.querySelector("#gm-movewrap"); existing?.remove();
    if (!moveId) return;
    const p = members.find((x) => x.userId === moveId); if (!p) { moveId = null; return; }
    const targets = teams().filter((t) => t.id !== p.teamId);
    const wrap = document.createElement("div");
    wrap.id = "gm-movewrap"; wrap.className = "gm-movewrap";
    wrap.innerHTML = `
      <div class="gm-movescrim" id="gm-movescrim"></div>
      <div class="gm-movesheet">
        <div class="sk-grab"><span></span></div>
        <div class="gm-move-h">Move <b>${esc(p.name)}</b></div>
        <div class="gm-move-sub">Assign to a different team</div>
        <div class="gm-move-list">
          ${targets.map((t) => `<button class="gm-movebtn" data-pick="${t.id}"><span class="gm-sdot" style="background:${TEAM_COLORS[teams().indexOf(t) % TEAM_COLORS.length]}"></span><span class="gm-mname">${esc(t.name)}</span><span class="gm-mtag">${t.role}</span></button>`).join("") || `<div class="sk-empty">No other teams.</div>`}
        </div>
        <div class="gm-move-list" style="margin-top:8px"><button class="gm-movebtn" id="gm-moveremove" style="color:var(--red)"><span class="gm-mname">Remove from game</span></button></div>
        <button class="sk-logout" id="gm-movecancel" style="margin-top:12px">Cancel</button>
      </div>`;
    root.querySelector(".gm")!.appendChild(wrap);
    const close = () => { moveId = null; wrap.remove(); };
    wrap.querySelector("#gm-movescrim")!.addEventListener("click", close);
    wrap.querySelector("#gm-movecancel")!.addEventListener("click", close);
    wrap.querySelector("#gm-moveremove")!.addEventListener("click", async () => {
      const uid = moveId ?? p.userId; close();
      if (!confirm(`Remove ${p.name} from this game?`)) return;
      try { await removeMember(client.gameId, uid); await reloadRoster(); } catch (e) { alert((e as Error).message); }
    });
    wrap.querySelectorAll<HTMLButtonElement>("[data-pick]").forEach((b) => b.addEventListener("click", async () => {
      const uid = moveId ?? p.userId; close();
      try { await setMemberTeam(client.gameId, uid, b.dataset.pick!); await reloadRoster(); } catch (e) { alert((e as Error).message); }
    }));
  }

  // ---- map overlay (off-limits draw + zone corner edit + layers) --------
  function openMap() {
    root.querySelector<HTMLElement>("#gm-mapwrap")!.hidden = false;
    if (!gameMap) {
      gameMap = new GameMap(root.querySelector<HTMLElement>("#map")!);
      gameMap.onStationClick = (id) => {
        gameMap!.select(id); gameMap!.flyToStation(id); gameMap!.showStationPopup(id);
        if (editing) stopEdit();
        zoneSel = id; renderMapCtl();
      };
      gameMap.refresh(client);
      mountMapLayers();
    }
    // Resize across a few frames — the overlay's final size isn't settled the
    // instant it's unhidden, which otherwise leaves the map a narrow strip.
    gameMap.map.resize();
    requestAnimationFrame(() => gameMap!.map.resize());
    setTimeout(() => gameMap!.map.resize(), 250);
    setTimeout(() => gameMap!.map.resize(), 600);
    renderMapCtl();
  }
  function closeMap() {
    if (editing) stopEdit();
    if (drawing) { drawing = false; gameMap!.captureClicks = false; gameMap!.onMapClick = null; gameMap!.clearPreviewGeometry(); }
    root.querySelector<HTMLElement>("#gm-mapwrap")!.hidden = true;
  }
  function startEdit() {
    if (!zoneSel || !gameMap) return;
    const key = overrideKey(client.config.zoneMode, zoneSel);
    const geom = client.zoneOverrides[key] ?? bakedZoneGeometry(zoneSel);
    editTeardown = gameMap.startZoneEdit(geom, (g) => client.setZoneOverride(key, g));
    editing = true; renderMapCtl();
  }
  function stopEdit() { editTeardown?.(); editTeardown = null; editing = false; }
  function startDraw() {
    if (!gameMap) return;
    ring = []; drawing = true; gameMap.captureClicks = true;
    gameMap.onMapClick = (ll) => { ring.push([ll.lng, ll.lat]); gameMap!.setPreviewGeometry(ring.length >= 2 ? [turf.lineString([...ring])] : []); renderMapCtl(); };
    renderMapCtl();
  }
  function finishDraw() {
    if (!gameMap) return;
    gameMap.captureClicks = false; gameMap.onMapClick = null; drawing = false;
    if (ring.length >= 3) client.offLimitsOp({ op: "add", area: { id: cryptoId(), name: "Drawn area", geometry: turf.polygon([[...ring, ring[0]]]).geometry } });
    gameMap.clearPreviewGeometry(); renderMapCtl();
  }
  function renderMapCtl() {
    const el = root.querySelector<HTMLElement>("#gm-mapctl")!;
    const isBlock = client.config.zoneMode.startsWith("block");
    const hasOverride = !!zoneSel && !!client.zoneOverrides[overrideKey(client.config.zoneMode, zoneSel)];
    el.innerHTML = `
      ${zoneSel && isBlock ? `<div class="gm-mapsel">Zone: <b>${esc(STATION_BY_ID[zoneSel].name)}</b>${hasOverride ? " · edited" : ""}
          <button class="${editing ? "on" : ""}" data-zedit>${editing ? "✓ Done" : "Edit corners"}</button>
          ${hasOverride ? `<button data-zreset>Reset</button>` : ""}</div>`
        : zoneSel && !isBlock ? `<div class="gm-mapsel">Corner editing applies to <b>block</b> zone sizes.</div>` : ""}
      <div class="gm-mapdraw">
        ${drawing ? `<button class="on" data-dfinish>Finish off-limits (${ring.length})</button>` : `<button data-dstart>Draw off-limits area</button>`}
      </div>`;
    el.querySelector("[data-zedit]")?.addEventListener("click", () => editing ? (stopEdit(), renderMapCtl()) : startEdit());
    el.querySelector("[data-zreset]")?.addEventListener("click", () => { if (zoneSel) client.clearZoneOverride(overrideKey(client.config.zoneMode, zoneSel)); if (editing) stopEdit(); renderMapCtl(); });
    el.querySelector("[data-dstart]")?.addEventListener("click", startDraw);
    el.querySelector("[data-dfinish]")?.addEventListener("click", finishDraw);
  }
  function mountMapLayers() {
    if (!gameMap) return;
    const gm = gameMap;
    const LAYERS: { label: string; ids: string[]; on: boolean }[] = [
      { label: "Mask", ids: ["mask-fill"], on: true },
      { label: "Zones", ids: ["zone-fill", "zone-outline"], on: true },
      { label: "Dots", ids: ["dot"], on: true },
      { label: "Off-limits", ids: ["offlimits-fill", "offlimits-line"], on: true },
      { label: "Airports", ids: ["airport-dot", "airport-label"], on: true },
    ];
    const strip = document.createElement("div");
    strip.className = "gm-maplayers jl-scroll";
    strip.innerHTML = LAYERS.map((l, i) => `<button class="${l.on ? "on" : ""}" data-ml="${i}">${l.label}</button>`).join("")
      + `<button data-hideelim>Hide elim.</button>`;
    root.querySelector<HTMLElement>("#gm-mapwrap")!.appendChild(strip);
    strip.querySelectorAll<HTMLButtonElement>("[data-ml]").forEach((b) => b.addEventListener("click", () => {
      const l = LAYERS[+b.dataset.ml!]; l.on = !l.on; b.classList.toggle("on", l.on);
      l.ids.forEach((id) => gm.setLayerVisible(id, l.on));
    }));
    let he = false;
    strip.querySelector("[data-hideelim]")!.addEventListener("click", (e) => { he = !he; (e.target as HTMLElement).classList.toggle("on", he); gm.setHideEliminated(he, client); });
  }

  client.subscribe(() => { gameMap?.refresh(client); render(); });
  setInterval(tick, 1000);
  render();
}
